import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  useTheme,
  Chip,
  alpha,
} from '@mui/material';
import {
  Home as HomeIcon,
  Apps as AppsIcon,
  ImageSearch as ImageSearchIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { agentCategories } from '../config/agentCategories';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  
  // Track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    [] // Start with all categories collapsed
  );

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isAgentSelected = (agentRoute: string) => {
    return location.pathname === agentRoute;
  };

  const isCategoryActive = (category: typeof agentCategories[0]) => {
    return category.agents.some(agent => location.pathname === agent.route);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation Items */}
      <List sx={{ pt: 1 }}>
        {/* Home button hidden for now */}
        {/*
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            selected={location.pathname === '/'}
            onClick={() => navigate('/')}
            sx={{
              borderRadius: 2,
              mx: 1,
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.18),
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: location.pathname === '/' ? theme.palette.primary.main : 'inherit' }}>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Home" 
              primaryTypographyProps={{
                fontWeight: location.pathname === '/' ? 600 : 400,
              }}
            />
          </ListItemButton>
        </ListItem>
        */}
        
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            selected={location.pathname === '/agents'}
            onClick={() => navigate('/agents')}
            sx={{
              borderRadius: 2,
              mx: 1,
              minHeight: 48,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.5, sm: 1 },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.18),
                },
              },
            }}
          >
            <ListItemIcon sx={{
              color: location.pathname === '/agents' ? theme.palette.primary.main : 'inherit',
              minWidth: { xs: 40, sm: 44 },
            }}>
              <AppsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Agents"
              primaryTypographyProps={{
                fontWeight: location.pathname === '/agents' ? 600 : 400,
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton
            selected={location.pathname === '/media'}
            onClick={() => navigate('/media')}
            sx={{
              borderRadius: 2,
              mx: 1,
              minHeight: 48,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.5, sm: 1 },
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.18),
                },
              },
            }}
          >
            <ListItemIcon sx={{
              color: location.pathname === '/media' ? theme.palette.primary.main : 'inherit',
              minWidth: { xs: 40, sm: 44 },
            }}>
              <ImageSearchIcon />
            </ListItemIcon>
            <ListItemText
              primary="Media"
              primaryTypographyProps={{
                fontWeight: location.pathname === '/media' ? 600 : 400,
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Categories */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.text.primary, 0.2),
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: alpha(theme.palette.text.primary, 0.3),
          },
        },
      }}>
        <List sx={{ pt: 0 }}>
          {agentCategories.map((category) => {
            const isExpanded = expandedCategories.includes(category.id);
            const isActive = isCategoryActive(category);
            const CategoryIcon = category.icon;

            return (
              <Box key={category.id}>
                {/* Category Header */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleCategory(category.id)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      minHeight: 48,
                      px: { xs: 2, sm: 2.5 },
                      py: { xs: 1.5, sm: 1 },
                      backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive 
                          ? alpha(theme.palette.primary.main, 0.08)
                          : alpha(theme.palette.action.hover, 0.08),
                      },
                    }}
                  >
                    <ListItemIcon 
                      sx={{ 
                        color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                        minWidth: { xs: 36, sm: 40 },
                      }}
                    >
                      <CategoryIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="body2"
                            noWrap
                            sx={{ 
                              fontWeight: isActive ? 600 : 500,
                              fontSize: { xs: '0.875rem', sm: '0.9rem' },
                              color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                            }}
                          >
                            {category.name}
                          </Typography>
                          <Chip 
                            label={category.agents.length} 
                            size="small"
                            sx={{ 
                              height: { xs: 16, sm: 18 },
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              backgroundColor: isActive 
                                ? alpha(theme.palette.primary.main, 0.15)
                                : alpha(theme.palette.action.hover, 0.12),
                              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                              '& .MuiChip-label': {
                                px: 0.75,
                              },
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            display: isExpanded ? 'none' : 'block',
                            lineHeight: 1.3,
                          }}
                        >
                          {category.description}
                        </Typography>
                      }
                    />
                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </ListItemButton>
                </ListItem>

                {/* Category Agents */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {category.agents.map((agent) => {
                      const AgentIcon = agent.icon;
                      const isSelected = isAgentSelected(agent.route);

                      return (
                        <ListItem key={agent.id} disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => agent.isImplemented && navigate(agent.route)}
                            disabled={!agent.isImplemented}
                            sx={{
                              pl: { xs: 3, sm: 4 },
                              pr: 2,
                              py: { xs: 1, sm: 0.75 },
                              mx: 1,
                              minHeight: 44,
                              borderRadius: 1.5,
                              '&.Mui-selected': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.18),
                                },
                              },
                              '&.Mui-disabled': {
                                opacity: 0.5,
                              },
                            }}
                          >
                            <ListItemText 
                              primary={
                                <Typography 
                                  variant="body2"
                                  noWrap
                                  sx={{ 
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    fontWeight: isSelected ? 500 : 400,
                                  }}
                                >
                                  {agent.title}
                                </Typography>
                              }
                              secondary={
                                !agent.isImplemented ? (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: theme.palette.text.disabled,
                                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                    }}
                                  >
                                    Coming soon
                                  </Typography>
                                ) : null
                              }
                            />
                            {!agent.isImplemented && (
                              <Chip 
                                label="Soon" 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                  opacity: 0.6,
                                  borderColor: theme.palette.text.disabled,
                                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                  height: { xs: 14, sm: 16 },
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                  },
                                }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default Navigation;